import { DatePipe, SlicePipe } from '@angular/common';
import { Component, computed, effect, inject, input, output, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AuthService } from '../../core/auth/auth.service';
import {
  OccurrenceAttachment, OccurrenceEvidence, OccurrenceFollowUp,
} from '../../core/occurrence/occurrence.models';
import { OccurrenceService } from '../../core/occurrence/occurrence.service';
import { ToastService } from '../../core/ui/toast.service';
import { ConfirmDialog } from '../confirm-dialog/confirm-dialog';
import { EvidenceDraft, EvidenceDraftFields, emptyEvidenceDraft, saveEvidenceDraft } from './evidence-draft';

/**
 * As evidências da ocorrência — entradas com data, descrição, notas e os ficheiros que as
 * suportam, e o formulário que as regista e corrige.
 *
 * <p>Vive na ficha e no separador Evidências do formulário, como o seguimento: o separador só
 * dizia "regista-se na página da ocorrência", e quem estava a editar tinha de fechar o painel.
 */
@Component({
  selector: 'app-occurrence-evidences',
  imports: [FormsModule, DatePipe, SlicePipe, ConfirmDialog, EvidenceDraftFields],
  templateUrl: './occurrence-evidences.html',
})
export class OccurrenceEvidences {
  private readonly service = inject(OccurrenceService);
  private readonly toasts = inject(ToastService);
  protected readonly auth = inject(AuthService);

  readonly occurrenceId = input.required<number>();
  /** Uma evidência mudou — a ficha usa-o para refrescar o que conta evidências. */
  readonly changed = output<void>();

  protected readonly evidences = signal<OccurrenceEvidence[]>([]);
  /** Entradas de seguimento, para se poder dizer de qual veio a evidência. */
  protected readonly followUps = signal<OccurrenceFollowUp[]>([]);
  protected readonly error = signal<string | null>(null);
  protected readonly showForm = signal(false);
  protected readonly saving = signal(false);
  /** Evidência a ser corrigida; null quando se está a registar uma. */
  protected readonly editingId = signal<number | null>(null);
  protected readonly draft = signal<EvidenceDraft>(emptyEvidenceDraft());
  protected draftFollowUpId: number | null = null;
  protected readonly uploading = signal<number | null>(null);
  protected readonly pendingRemoval = signal<OccurrenceEvidence | null>(null);
  protected readonly pendingAttachmentRemoval = signal<OccurrenceAttachment | null>(null);
  protected readonly canWrite = computed(() => this.auth.can('occurrence:write'));

  /** Quantas evidências existem — quem o aloja mostra o número no cabeçalho. */
  readonly count = computed(() => this.evidences().length);

  constructor() {
    // O id chega por input e muda sem o componente ser destruído (abrir outra ocorrência).
    // O load fica fora do rastreio: lê o tenant da sessão, e ao sair a sessão esvaziava-se e o
    // efeito repetia os pedidos para /t/null.
    effect(() => {
      this.occurrenceId();
      untracked(() => this.load());
    });
  }

  /** Volta a ler a lista — o seguimento pode ter registado uma evidência entretanto. */
  reload(): void {
    this.load();
  }

  private load(): void {
    this.service.evidences(this.occurrenceId()).subscribe({
      next: (list) => this.evidences.set(list),
      error: () => this.evidences.set([]),
    });
    this.service.followUps(this.occurrenceId()).subscribe({
      next: (list) => this.followUps.set(list),
      error: () => this.followUps.set([]),
    });
  }

  /** Abre o registo — também a partir da Análise, que tem o seu "Registar evidência". */
  openForm(): void {
    this.draft.set(emptyEvidenceDraft());
    this.draftFollowUpId = null;
    this.editingId.set(null);
    this.error.set(null);
    this.showForm.set(true);
  }

  /** "Não permite editar" (revisão de 2026-10-13): corrige-se aqui, sem apagar os ficheiros. */
  protected openEdit(evidence: OccurrenceEvidence): void {
    this.draft.set({
      evidenceDate: evidence.evidenceDate,
      description: evidence.description,
      notes: evidence.notes ?? '',
      files: [],
    });
    this.draftFollowUpId = evidence.followUpId;
    this.editingId.set(evidence.id);
    this.error.set(null);
    this.showForm.set(true);
  }

  protected cancelForm(): void {
    this.showForm.set(false);
  }

  protected save(): void {
    if (!this.draft().description.trim()) {
      this.error.set('Descreva a evidência.');
      return;
    }
    this.error.set(null);
    this.saving.set(true);
    const editing = this.editingId();
    saveEvidenceDraft(this.service, this.occurrenceId(), this.draft(), this.draftFollowUpId, editing)
      .subscribe({
        next: ({ failedFiles }) => {
          this.saving.set(false);
          this.showForm.set(false);
          this.load();
          this.changed.emit();
          if (failedFiles.length) {
            this.toasts.error(`A evidência ficou gravada, mas não foi possível enviar: ${failedFiles.join(', ')}.`);
          } else {
            this.toasts.success(editing === null ? 'Evidência registada.' : 'Evidência atualizada.');
          }
        },
        error: () => {
          this.saving.set(false);
          this.error.set(editing === null
            ? 'Não foi possível registar a evidência.'
            : 'Não foi possível guardar a evidência.');
        },
      });
  }

  protected askRemove(evidence: OccurrenceEvidence): void {
    this.pendingRemoval.set(evidence);
  }

  protected confirmRemove(): void {
    const evidence = this.pendingRemoval();
    if (!evidence) return;
    this.pendingRemoval.set(null);
    this.service.deleteEvidence(this.occurrenceId(), evidence.id).subscribe({
      next: () => {
        this.load();
        this.changed.emit();
        this.toasts.success('Evidência removida.');
      },
      error: () => this.error.set('Não foi possível remover a evidência.'),
    });
  }

  /** Anexar a uma evidência já registada, sem abrir o formulário. */
  protected onFilesSelected(event: Event, evidenceId: number): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    input.value = '';
    if (!files.length) return;
    this.error.set(null);
    this.uploading.set(evidenceId);
    let pending = files.length;
    const done = () => {
      if (--pending === 0) {
        this.uploading.set(null);
        this.load();
      }
    };
    for (const file of files) {
      this.service.uploadAttachment(this.occurrenceId(), evidenceId, file).subscribe({
        next: done,
        error: () => {
          this.error.set(`Não foi possível enviar "${file.name}".`);
          done();
        },
      });
    }
  }

  /** Remoção de ficheiro: pede confirmação no diálogo da app (era um `confirm()` do browser). */
  protected askRemoveAttachment(attachment: OccurrenceAttachment): void {
    this.pendingAttachmentRemoval.set(attachment);
  }

  protected confirmRemoveAttachment(): void {
    const attachment = this.pendingAttachmentRemoval();
    if (!attachment) return;
    this.pendingAttachmentRemoval.set(null);
    this.service.deleteAttachment(this.occurrenceId(), attachment.id).subscribe({
      next: () => {
        this.load();
        this.toasts.success('Ficheiro removido.');
      },
      error: () => this.error.set('Não foi possível remover o ficheiro.'),
    });
  }

  protected fileIcon(contentType: string | null): string {
    if (!contentType) return 'fe-file';
    if (contentType.startsWith('image/')) return 'fe-image';
    if (contentType === 'application/pdf') return 'fe-file-text';
    if (contentType === 'text/plain') return 'fe-file-text';
    return 'fe-file';
  }

  protected formatSize(bytes: number): string {
    if (bytes >= 1_048_576) return `${(bytes / 1_048_576).toFixed(1)} MB`;
    if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${bytes} B`;
  }
}
